import { useEffect, useState } from "react";
import { Cpu, Shield, Wallet, Zap, X, Play, Loader2, Sparkles, Activity, ShieldCheck } from "lucide-react";
import { PageHeader, Card, SectionTitle } from "@/components/common/PageHeader";
import { StatusChip } from "@/components/common/StatusChip";
import { Loading, Bar } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

const CLASS_COLORS = {
  "PUBLIC": "#64748b", "INTERNAL": "#0ea5e9", "CONFIDENTIAL": "#8b5cf6",
  "SENSITIVE FINANCIAL": "#1a5fd0", "RESTRICTED IDENTITY": "#e11d48",
};

function WorkPanel({ spec, onRan }) {
  const [task, setTask] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (t) => {
    const text = (t ?? task).trim();
    if (!text || running) return;
    setTask(text);
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/agents/run", { agent: spec.name, task: text });
      setResult(data);
      onRan && onRan();
    } catch {
      toast.error("Agent could not complete the task. Please try again.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="mt-5 rounded-xl border border-[#1a5fd0]/20 bg-[#1a5fd0]/[0.03] p-4" data-testid="agent-work-panel">
      <div className="flex items-center gap-2">
        <Sparkles size={14} className="text-[#1a5fd0]" />
        <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-[#1a5fd0]">Put {spec.name} to work</span>
      </div>
      <p className="mt-1 text-[12px] text-slate-500">{spec.blurb} Runs on the live MMT model router, grounded in your authorized data.</p>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {spec.tasks.map((t) => (
          <button key={t} onClick={() => run(t)} disabled={running} data-testid="agent-task-chip"
            className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] text-slate-600 transition-colors hover:border-[#1a5fd0]/50 hover:text-[#1a5fd0] disabled:opacity-50">
            {t}
          </button>
        ))}
      </div>

      <div className="mt-3 flex items-end gap-2">
        <textarea rows={1} value={task} onChange={(e) => setTask(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); run(); } }}
          placeholder={`Assign a task to ${spec.name}…`} data-testid="agent-task-input"
          className="flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-[#1a5fd0]" />
        <button onClick={() => run()} disabled={running || !task.trim()} data-testid="agent-run-btn"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#080d1a] text-white transition-opacity disabled:opacity-40">
          {running ? <Loader2 size={16} className="animate-spin" /> : <Play size={15} />}
        </button>
      </div>

      {running && (
        <div className="mt-3 flex items-center gap-2 text-[12px] text-slate-500" data-testid="agent-running">
          <Loader2 size={13} className="animate-spin text-[#1a5fd0]" /> {spec.name} is working on your task…
        </div>
      )}

      {result && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3.5" data-testid="agent-result">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-mono text-[9px] uppercase tracking-wider text-slate-400">Work product · draft for review</span>
            <span className="font-mono text-[9px] font-semibold text-[#1a5fd0]">{result.provider}:{result.model}</span>
          </div>
          <div className="whitespace-pre-wrap text-[13px] leading-relaxed text-slate-700">{result.result}</div>
          <div className="mt-2.5 flex items-center gap-1.5 border-t border-slate-100 pt-2 text-[10px] text-slate-400">
            <ShieldCheck size={12} className="text-emerald-500" /> Requires human approval before any real action is taken.
          </div>
        </div>
      )}
    </div>
  );
}

function AgentDrawer({ agent, spec, onClose, onRan }) {
  if (!agent) return null;
  const rows = [
    ["Supervisor", agent.supervisor], ["Mission", agent.mission],
    ["Tools", agent.tools.join(", ")], ["Max data class", agent.max_data_class],
    ["Approvals", agent.approvals], ["Current work", agent.current_work],
    ["Cost (MTD)", agent.cost_mtd],
  ];
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="h-full w-full max-w-md overflow-y-auto border-l border-slate-200 bg-white p-6 animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="agent-drawer">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#080d1a] font-head text-lg font-bold text-white">{agent.name[0]}</div>
            <div>
              <div className="font-head text-lg font-extrabold text-slate-900">{agent.name}</div>
              <div className="text-[12px] text-slate-500">{agent.role}</div>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="agent-drawer-close"><X size={18} /></button>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <StatusChip state={agent.state} />
          <span className={`font-mono text-[10px] uppercase tracking-wider ${agent.status === "Active" ? "text-emerald-600" : "text-slate-400"}`}>● {agent.status}</span>
        </div>

        {spec && <WorkPanel spec={spec} onRan={onRan} />}

        <div className="mt-5 space-y-3">
          {rows.map(([k, v]) => (
            <div key={k} className="border-b border-slate-100 pb-2.5">
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#1a5fd0]">{k}</div>
              <div className="mt-0.5 text-[13px] text-slate-700">{v}</div>
            </div>
          ))}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-[#1a5fd0]">Performance</div>
            <div className="mt-1.5 flex items-center gap-2"><Bar pct={agent.performance} /><span className="font-mono text-[12px] text-slate-600">{agent.performance}%</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActivityFeed({ activity }) {
  if (!activity) return null;
  const { runs = [], feed = [] } = activity;
  return (
    <Card className="p-5" data-testid="activity-feed">
      <SectionTitle icon={Activity}>Live Workforce Activity</SectionTitle>
      <p className="mb-3 text-[13px] text-slate-500">What your AI workforce has been doing. Tasks you assign appear here instantly.</p>
      <div className="space-y-2">
        {runs.map((r, i) => (
          <div key={`run-${i}`} className="flex items-start justify-between rounded-lg border border-[#1a5fd0]/20 bg-[#1a5fd0]/[0.03] px-4 py-2.5" data-testid="activity-run">
            <div className="min-w-0">
              <div className="text-[13px] font-medium text-slate-800">{r.agent} · <span className="font-normal text-slate-500">{r.task}</span></div>
              <div className="font-mono text-[9px] uppercase tracking-wider text-[#1a5fd0]">{r.provider}:{r.model} · {r.auto ? "auto-run" : "completed"}</div>
            </div>
            <div className="ml-3 flex shrink-0 items-center gap-2">
              {r.auto && <span className="rounded-full bg-[#b0862f]/10 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-[#b0862f]" data-testid="activity-auto-badge">Auto</span>}
              <span className="font-mono text-[10px] text-slate-400">{String(r.when).slice(0, 10)}</span>
            </div>
          </div>
        ))}
        {feed.map((a, i) => (
          <div key={`feed-${i}`} className="flex items-start justify-between rounded-lg border border-slate-100 px-4 py-2.5" data-testid="activity-item">
            <div className="min-w-0">
              <div className="text-[13px] font-medium text-slate-800">{a.agent} <span className="font-normal text-slate-500">· {a.action}</span></div>
              <span className="mt-0.5 inline-block font-mono text-[9px] font-semibold uppercase tracking-wider" style={{ color: CLASS_COLORS[a.class] }}>{a.class}</span>
            </div>
            <span className="ml-3 shrink-0 font-mono text-[10px] text-slate-400">{a.when}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function AIWorkforce() {
  const [wf, setWf] = useState(null);
  const [router, setRouter] = useState(null);
  const [assignable, setAssignable] = useState({});
  const [activity, setActivity] = useState(null);
  const [active, setActive] = useState(null);

  const loadActivity = () => api.get("/ai/activity").then(({ data }) => setActivity(data)).catch(() => {});

  useEffect(() => {
    api.get("/ai/workforce").then(({ data }) => setWf(data)).catch(() => {});
    api.get("/ai/router").then(({ data }) => setRouter(data)).catch(() => {});
    api.get("/ai/agents").then(({ data }) => {
      const map = {};
      (data.agents || []).forEach((a) => { map[a.name] = a; });
      setAssignable(map);
    }).catch(() => {});
    loadActivity();
  }, []);

  if (!wf) return <Loading />;
  const totalAgents = wf.departments.reduce((n, d) => n + d.agents.length, 0);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="ai-workforce-page">
      <PageHeader eyebrow="AI Workforce / Control Room" title="AI Workforce Control Room"
        subtitle="A real, orchestrated AI workforce. Assign tasks to LIVE agents and watch them work — every agent has a mission, supervisor, tools, data permissions, spend limits and audit history."
        badge={<StatusChip state="LIVE" />} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="p-4"><div className="flex items-center gap-2 text-slate-400"><Cpu size={15} /><span className="font-mono text-[10px] uppercase">Agents</span></div><div className="mt-1 font-head text-2xl font-extrabold text-slate-900">{totalAgents}</div></Card>
        <Card className="p-4"><div className="flex items-center gap-2 text-slate-400"><Zap size={15} /><span className="font-mono text-[10px] uppercase">Departments</span></div><div className="mt-1 font-head text-2xl font-extrabold text-slate-900">{wf.departments.length}</div></Card>
        <Card className="p-4"><div className="flex items-center gap-2 text-slate-400"><Shield size={15} /><span className="font-mono text-[10px] uppercase">Human approval</span></div><div className="mt-1 font-head text-2xl font-extrabold text-slate-900">Enforced</div></Card>
        <Card className="p-4"><div className="flex items-center gap-2 text-slate-400"><Wallet size={15} /><span className="font-mono text-[10px] uppercase">Spend controls</span></div><div className="mt-1 font-head text-2xl font-extrabold text-slate-900">Active</div></Card>
      </div>

      <ActivityFeed activity={activity} />

      {router && (
        <Card className="p-5">
          <SectionTitle icon={Cpu}>Multi-Model AI Router</SectionTitle>
          <p className="mb-3 text-[13px] text-slate-500">Agents request a capability class, not a fixed model. The router selects a provider by task, sensitivity, cost, latency and availability.</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(router.routing_table).map(([cap, r]) => (
              <div key={cap} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2">
                <span className="font-mono text-[11px] text-slate-600">{cap}</span>
                <span className="font-mono text-[10px] font-semibold text-[#1a5fd0]">{r.provider}:{r.model}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="space-y-6">
        {wf.departments.map((dept) => (
          <div key={dept.name}>
            <SectionTitle icon={Cpu}>{dept.name}</SectionTitle>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {dept.agents.map((a) => {
                const canWork = !!assignable[a.name];
                return (
                  <button key={a.name} onClick={() => setActive(a)} data-testid="agent-card"
                    className="rounded-xl border border-slate-200 bg-white p-4 text-left transition-shadow hover:shadow-md">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#080d1a] font-head text-sm font-bold text-white">{a.name[0]}</div>
                        <div>
                          <div className="font-head text-[14px] font-bold text-slate-900">{a.name}</div>
                          <div className="text-[11px] text-slate-500">{a.role}</div>
                        </div>
                      </div>
                      <StatusChip state={a.state} />
                    </div>
                    <p className="mt-2.5 line-clamp-2 text-[12px] text-slate-500">{a.mission}</p>
                    <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2.5">
                      {canWork
                        ? <span className="inline-flex items-center gap-1 font-mono text-[10px] font-semibold uppercase text-[#1a5fd0]"><Sparkles size={11} /> Assignable</span>
                        : <span className={`font-mono text-[10px] uppercase ${a.status === "Active" ? "text-emerald-600" : "text-slate-400"}`}>● {a.status}</span>}
                      <span className="font-mono text-[10px] text-slate-400">{a.cost_mtd} MTD</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <AgentDrawer agent={active} spec={active ? assignable[active.name] : null} onClose={() => setActive(null)} onRan={loadActivity} />
    </div>
  );
}
